import { baseApi } from './baseApi'
import { API_ENDPOINTS } from '@/constants'
import type {
  ApiResponse,
  PaginatedResponse,
  EstrusEvent,
  CreateEstrusEventData,
  EstrusEventFilters,
} from '@/types'

export const estrusApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ===================================
    // Queries (GET)
    // ===================================

    /**
     * Get all estrus events with filters
     */
    getEstrusEvents: builder.query<PaginatedResponse<EstrusEvent>, EstrusEventFilters | void>({
      query: (filters) => ({
        url: API_ENDPOINTS.ESTRUS.BASE,
        params: filters || {},
      }),
      transformResponse: (response: ApiResponse<PaginatedResponse<EstrusEvent>>) =>
        response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Estrus' as const, id })),
              { type: 'Estrus', id: 'LIST' },
            ]
          : [{ type: 'Estrus', id: 'LIST' }],
    }),

    /**
     * Get estrus event by ID
     */
    getEstrusEventById: builder.query<EstrusEvent, string>({
      query: (id) => API_ENDPOINTS.ESTRUS.DETAIL(id),
      transformResponse: (response: ApiResponse<EstrusEvent>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Estrus', id }],
    }),

    /**
     * Get estrus events by cow ID
     */
    getEstrusEventsByCow: builder.query<
      PaginatedResponse<EstrusEvent>,
      { cowId: string; filters?: EstrusEventFilters }
    >({
      query: ({ cowId, filters }) => ({
        url: API_ENDPOINTS.ESTRUS.BY_COW(cowId),
        params: filters || {},
      }),
      transformResponse: (response: ApiResponse<PaginatedResponse<EstrusEvent>>) =>
        response.data,
      providesTags: (_result, _error, { cowId }) => [
        { type: 'Estrus', id: `COW_${cowId}` },
      ],
    }),

    /**
     * Get estrus events by farm ID
     */
    getEstrusEventsByFarm: builder.query<
      PaginatedResponse<EstrusEvent>,
      { farmId: string; filters?: EstrusEventFilters }
    >({
      query: ({ farmId, filters }) => ({
        url: API_ENDPOINTS.ESTRUS.BY_FARM(farmId),
        params: filters || {},
      }),
      transformResponse: (response: ApiResponse<PaginatedResponse<EstrusEvent>>) =>
        response.data,
      providesTags: (_result, _error, { farmId }) => [
        { type: 'Estrus', id: `FARM_${farmId}` },
      ],
    }),

    /**
     * Get active estrus events (currently in estrus)
     */
    getActiveEstrusEvents: builder.query<EstrusEvent[], void>({
      query: () => API_ENDPOINTS.ESTRUS.ACTIVE,
      transformResponse: (response: ApiResponse<EstrusEvent[]>) => response.data,
      providesTags: [{ type: 'Estrus', id: 'ACTIVE' }],
    }),

    /**
     * Get estrus calendar view
     */
    getEstrusCalendar: builder.query<
      { date: string; events: EstrusEvent[] }[],
      { startDate?: string; endDate?: string; farmId?: string }
    >({
      query: (params) => ({
        url: API_ENDPOINTS.ESTRUS.CALENDAR,
        params,
      }),
      transformResponse: (response: ApiResponse<{ date: string; events: EstrusEvent[] }[]>) =>
        response.data,
      providesTags: [{ type: 'Estrus', id: 'CALENDAR' }],
    }),

    // ===================================
    // Mutations (POST, PUT, DELETE)
    // ===================================

    /**
     * Create a new estrus event
     */
    createEstrusEvent: builder.mutation<EstrusEvent, CreateEstrusEventData>({
      query: (data) => ({
        url: API_ENDPOINTS.ESTRUS.CREATE,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<EstrusEvent>) => response.data,
      invalidatesTags: (_result, _error, data) => [
        { type: 'Estrus', id: 'LIST' },
        { type: 'Estrus', id: `COW_${data.cowId}` },
        { type: 'Estrus', id: 'ACTIVE' },
        { type: 'Estrus', id: 'CALENDAR' },
        { type: 'Notification', id: 'LIST' }, // Trigger notification refresh
      ],
    }),

    /**
     * Update an estrus event
     */
    updateEstrusEvent: builder.mutation<
      EstrusEvent,
      { id: string; data: Partial<CreateEstrusEventData> }
    >({
      query: ({ id, data }) => ({
        url: API_ENDPOINTS.ESTRUS.UPDATE(id),
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<EstrusEvent>) => response.data,
      invalidatesTags: (result, _error, { id }) => [
        { type: 'Estrus', id },
        { type: 'Estrus', id: 'LIST' },
        { type: 'Estrus', id: 'ACTIVE' },
        { type: 'Estrus', id: 'CALENDAR' },
        ...(result ? [{ type: 'Estrus' as const, id: `COW_${result.cowId}` }] : []),
      ],
    }),

    /**
     * Delete an estrus event
     */
    deleteEstrusEvent: builder.mutation<void, string>({
      query: (id) => ({
        url: API_ENDPOINTS.ESTRUS.DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Estrus', id },
        { type: 'Estrus', id: 'LIST' },
        { type: 'Estrus', id: 'ACTIVE' },
        { type: 'Estrus', id: 'CALENDAR' },
      ],
    }),
  }),
})

export const {
  useGetEstrusEventsQuery,
  useGetEstrusEventByIdQuery,
  useGetEstrusEventsByCowQuery,
  useGetEstrusEventsByFarmQuery,
  useGetActiveEstrusEventsQuery,
  useGetEstrusCalendarQuery,
  useCreateEstrusEventMutation,
  useUpdateEstrusEventMutation,
  useDeleteEstrusEventMutation,
} = estrusApi
